import { useRouter } from 'expo-router';
import { Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Brand, Neutrals } from '@/constants/theme';

const CONTENT = {
  title: 'Privacy Policy',
  lastUpdated: 'September 25, 2026',
  intro:
    'Sage Chef ("we", "our", or "the app") is committed to protecting your privacy. This policy describes how we collect, use, and share information when you use our web and mobile applications.',
  sections: [
    {
      heading: '1. Information We Collect',
      body: 'We collect information you provide directly (such as email, name, and password when you create an account), content you create (recipes, grocery lists, household data), and usage data. When you use the app, we also collect device and usage information for analytics and error reporting.',
    },
    {
      heading: '2. How We Use Your Information',
      body: 'We use your information to provide and improve the service (e.g., syncing your recipes and grocery lists across devices, managing household members and invites), to communicate with you about your account, and to understand how the app is used so we can improve it. We do not sell your personal information.',
    },
    {
      heading: '3. Third-Party Services',
      body: 'We use the following third-party services:\n\n• **Authentication and database (Supabase)** – Your account data and app content (recipes, grocery lists, profiles) are stored and processed by Supabase. Their privacy policy applies to that processing.\n\n• **Analytics (Amplitude)** – We use Amplitude to understand usage (e.g., screen views, feature usage) in aggregate. This helps us improve the product. We do not send personally identifiable information beyond what is needed for basic analytics.\n\n• **Error reporting (Sentry)** – We use Sentry to capture errors and performance issues so we can fix bugs. Error reports may include limited technical data (e.g., device type, app version); we configure Sentry to avoid sending unnecessary personal data.',
    },
    {
      heading: '4. Data Retention and Security',
      body: 'We retain your data for as long as your account is active or as needed to provide the service. We use industry-standard measures to protect your data in transit and at rest. Our infrastructure providers (including Supabase) apply their own security and compliance practices.',
    },
    {
      heading: '5. Your Rights',
      body: 'Depending on your location, you may have the right to access, correct, or delete your personal data, or to export it. You can update your profile and account details in the app.\n\nTo permanently delete your account and associated data, open Settings, scroll to Delete account at the bottom, then confirm. Deletion removes your profile, recipes, grocery lists, and related account data and cannot be undone. For other privacy rights, contact us (see below).',
    },
    {
      heading: '6. Children',
      body: 'The app is not directed at children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us so we can delete it.',
    },
    {
      heading: '7. Changes to This Policy',
      body: 'We may update this privacy policy from time to time. We will post the updated policy at this URL and update the "Last updated" date. Continued use of the app after changes constitutes acceptance of the updated policy.',
    },
    {
      heading: '8. Contact',
      body: 'For questions about this privacy policy or your data, contact us through the in-app Settings screen or at the support contact provided in the app store listing.',
    },
  ],
};

function Section({ heading, body }: { heading: string; body: string }) {
  const parts = body.split(/\n\n/);
  return (
    <View style={styles.section}>
      <Text style={styles.sectionHeading}>{heading}</Text>
      {parts.map((paragraph, i) => (
        <Text key={i} style={styles.paragraph}>
          {paragraph.replace(/\*\*(.*?)\*\*/g, '$1')}
        </Text>
      ))}
    </View>
  );
}

export default function PrivacyScreen() {
  const router = useRouter();

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.container}
      showsVerticalScrollIndicator={true}
    >
      <View style={styles.header}>
        <Text style={styles.title}>{CONTENT.title}</Text>
        <Text style={styles.updated}>Last updated: {CONTENT.lastUpdated}</Text>
      </View>
      <Text style={styles.intro}>{CONTENT.intro}</Text>
      {CONTENT.sections.map((section, index) => (
        <Section key={index} heading={section.heading} body={section.body} />
      ))}
      {Platform.OS === 'web' && (
        <View style={styles.footer}>
          <Text
            style={styles.link}
            onPress={() => router.replace('/(auth)/sign-in')}
          >
            Back to Sign in
          </Text>
        </View>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    backgroundColor: '#FAFAF9',
  },
  container: {
    maxWidth: 720,
    width: '100%',
    alignSelf: 'center',
    padding: 24,
    paddingBottom: 48,
  },
  header: {
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: Neutrals.textPrimary,
    marginBottom: 4,
  },
  updated: {
    fontSize: 14,
    color: Neutrals.textSecondary,
  },
  intro: {
    fontSize: 16,
    lineHeight: 24,
    color: Neutrals.textPrimary,
    marginBottom: 24,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeading: {
    fontSize: 18,
    fontWeight: '600',
    color: Brand.primaryDark,
    marginBottom: 8,
  },
  paragraph: {
    fontSize: 15,
    lineHeight: 22,
    color: Neutrals.textPrimary,
    marginBottom: 12,
  },
  footer: {
    marginTop: 24,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: Neutrals.border,
  },
  link: {
    fontSize: 16,
    color: Brand.primary,
    fontWeight: '500',
  },
});
